import React, { FC, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';

import VariationName from './VariationName';
import { useGetModificationGroups } from '~Hooks/ItemModifications/api/useGetModificationGroups';
import { useStorage } from '~Components/Storage';
import { useLogger } from '~Hooks';
import {
  buildVariationOrderMap,
  sortVariationIdsByProductOrder,
} from '../sortVariationsByProductOrder';

const Wrapper = styled.div`
  margin: 0 16px 0 56px;
  color: #585858;
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
`;

const VariationItem = styled.div<{ deleted?: boolean; markGreen?: boolean }>`
  margin: 0 0 5px 0;
  font-size: 24px;
  border-radius: 5px;
  line-height: 40px;
  color: ${(props) => {
    if (props.deleted) return 'var(--colorUIRed)';
    if (props.markGreen) return 'var(--colorUIGreen)';
    return 'var(--colorBlueGray1200)';
  }};
  font-weight: ${(props) => (props.deleted || props.markGreen ? 600 : 400)};

  span {
    margin-right: 8px;
  }
`;

interface Props {
  /** The product's modification group ids, in backoffice order. Used to sort the variations and to identify if a variation was preselected or not */
  modificationGroups: string[];
  variations: string[];
  removedVariations: string[];
}

const Variations: FC<Props> = ({ modificationGroups, variations, removedVariations }) => {
  const [storage] = useStorage();
  const logger = useLogger('kds-variations');

  /** Raw groups read from the Apollo cache, in the product's order. Groups missing from the cache are filtered out */
  const groups = useGetModificationGroups(modificationGroups);

  useEffect(() => {
    if (groups.length < modificationGroups.length) {
      // Deleted groups stay in Item.modificationGroups, so this is expected now and then.
      // Variations in missing groups keep their incoming (backend) order at the end.
      logger.debug('Some modification groups were not found in the cache', {
        modificationGroupIds: modificationGroups,
        foundModificationGroupIds: groups.map((group) => group.id),
      });
    }
  }, [groups, modificationGroups]);

  /** variationId -> position in the product (group order, then order inside the group) */
  const variationOrderMap = useMemo(() => buildVariationOrderMap(groups), [groups]);

  /** All variations that are preselected in any of the product's groups */
  const preselectedVariationIds = useMemo(
    () => new Set(groups.flatMap((group) => group.preselectedVariations ?? [])),
    [groups],
  );

  /** The backend sends variations sorted by id - show them in the product's modification group order instead */
  const sortedVariations = useMemo(
    () => sortVariationIdsByProductOrder(variations, variationOrderMap),
    [variations, variationOrderMap],
  );
  const sortedRemovedVariations = useMemo(
    () => sortVariationIdsByProductOrder(removedVariations, variationOrderMap),
    [removedVariations, variationOrderMap],
  );

  const isPreselected = (id: string) => preselectedVariationIds.has(id);

  const getGreenMarkedVariations = (): {
    regularVariations: string[];
    greenMarkedVariations: string[];
  } => {
    if (!storage.markAddedVariationsGreen) {
      return { regularVariations: sortedVariations, greenMarkedVariations: [] };
    }

    const preselectedVariations = sortedVariations.filter((id) => isPreselected(id));
    const greenMarkedVariations = sortedVariations.filter((id) => !isPreselected(id));
    return { regularVariations: preselectedVariations, greenMarkedVariations };
  };

  /** Regular variations is just variations with no styling - greenMarked is the ones that will have a + prefix and green */
  const { regularVariations, greenMarkedVariations } = getGreenMarkedVariations();

  // Keys include the index since the same variation id can in theory appear more than once
  return (
    <Wrapper>
      {sortedRemovedVariations.map((id, index) => (
        <VariationItem key={`${id}-${index}`} deleted>
          <span>-</span>
          <VariationName id={id} deleted />
        </VariationItem>
      ))}

      {regularVariations.map((id, index) => (
        <VariationItem key={`${id}-${index}`}>
          <span>·</span>
          <VariationName id={id} />
        </VariationItem>
      ))}

      {greenMarkedVariations.map((id, index) => (
        <VariationItem key={`${id}-${index}`} markGreen>
          <span>+</span>
          <VariationName id={id} markGreen />
        </VariationItem>
      ))}
    </Wrapper>
  );
};

export default React.memo(Variations);
